/**
 * Guest Space view — ``/connect/space/$spaceId`` (credential-gated, no auth).
 *
 * Where a magic-link guest lands after accepting their invite. It is gated by
 * the SPACE-SCOPED guest credential persisted at accept time
 * (``readGuestCredential(spaceId)``): present → branded chat; absent → a calm
 * prompt to (re)open the invite link. The credential is replayed as
 * ``Authorization: ConnectGuest <token>`` through ``askInSpace`` — it is NEVER
 * the owner's tenant session, only this one space's narrow token.
 *
 * Standalone (chrome-less) layout, reusing the SAME ``<ConnectChat>`` +
 * ``<SpaceScopeBanner>`` as the authed owner console — the only difference is
 * the injected transport. DRY: no second chat is built here.
 */

import {
  ConnectChat,
  ConnectFocusHeading,
  ConnectMessageThread,
  ConnectPublicNotice,
  ConnectPublicPage,
  ReadOnlyScopeLine,
  SpaceScopeBanner,
  readGuestCredential,
} from "@/features/connect";
import {
  type ConnectAskResponse,
  type ConnectScopeSummary,
  askInSpace,
  getSpaceScope,
} from "@ando/core";
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MessagesSquare, Sparkles } from "lucide-react";
import * as React from "react";

export const Route = createFileRoute("/connect/space/$spaceId")({
  component: GuestSpaceView,
});

function GuestSpaceView(): React.JSX.Element {
  const { i18n } = useLingui();
  const { spaceId } = Route.useParams();

  // Synchronous, space-scoped read — decided on first render (no async flash).
  const credential = React.useMemo(() => readGuestCredential(spaceId), [spaceId]);

  // Guest-credential-aware scope fetch — the FULL normalized resource list +
  // the owning org name, resolved through the SAME ConnectGuest credential as
  // ``askInSpace``. This replaces the old "doc count only" placeholder: the
  // guest now sees EXACTLY the chips the owner shares.
  const scope = useQuery<ConnectScopeSummary>({
    queryKey: ["connect-space-scope", spaceId],
    queryFn: () => getSpaceScope(spaceId, credential?.credential),
    enabled: Boolean(credential),
    retry: false,
    staleTime: 60_000,
  });

  // Bind the guest transport: the credential rides as the ConnectGuest header.
  const ask = React.useCallback(
    async (question: string): Promise<ConnectAskResponse> => {
      return askInSpace(spaceId, { question }, credential?.credential);
    },
    [spaceId, credential],
  );

  // ── No stored credential → prompt to use the invite link ─────────────
  if (!credential) {
    return (
      <ConnectPublicNotice
        reason="notfound"
        title={<Trans>Apri il tuo link di invito</Trans>}
        body={
          <Trans>
            Per accedere a questo spazio usa il link di invito che hai ricevuto via email. Da lì
            potrai entrare e fare domande.
          </Trans>
        }
      />
    );
  }

  const ownerOrgName = scope.data?.owner_org_name?.trim() || null;
  const resources = scope.data?.resources;
  const hasResources = Boolean(resources && resources.length > 0);

  return (
    <ConnectPublicPage label={i18n._(msg`Spazio condiviso Ando Connect`)} className="!py-0">
      {/* Constrain to a comfortable, mobile-first reading column. */}
      <div className="flex min-h-dvh w-full max-w-2xl flex-col gap-3 py-4 sm:py-6">
        {/* Branded collaboration header — "Stai collaborando con {org}". */}
        <header className="flex items-center gap-3 px-1">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ando-orange-soft text-ando-orange"
          >
            <Sparkles size={18} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="font-medium text-[11.5px] text-ink-2">
              <Trans>Stai collaborando con</Trans>
            </p>
            <ConnectFocusHeading className="truncate text-[18px]">
              {ownerOrgName ?? i18n._(msg`Spazio condiviso`)}
            </ConnectFocusHeading>
          </div>
        </header>

        <ConnectChat
          ask={ask}
          className={cn("min-h-0 flex-1")}
          scopeBanner={
            hasResources ? (
              // Full server-resolved scope — the SAME chip banner the owner
              // sees, fed from the scope endpoint's normalized resources.
              <SpaceScopeBanner
                readOnly
                heading={<Trans>Interroghi solo le fonti condivise con te</Trans>}
                resources={resources}
              />
            ) : (
              // No resources resolved yet (loading / older BE) — calm read-only
              // assurance line, never the owner-framed "nothing shared" copy.
              <ReadOnlyScopeLine />
            )
          }
          subtitle={credential.email ? i18n._(msg`Accesso come ${credential.email}`) : undefined}
          emptyHint={i18n._(
            msg`Fai una domanda. Ando risponde solo dalle fonti condivise con te in questo spazio.`,
          )}
        />

        {/* Member-to-member discussion — the SAME human thread the owner +
            members see, with the guest's stored ConnectGuest credential so a
            magic-link guest can participate. DISTINCT from the AI chat above. */}
        <section className="flex min-h-[22rem] flex-col">
          <h2 className="flex items-center gap-1.5 px-1 pb-2 font-medium text-[11.5px] text-ink-2">
            <MessagesSquare size={13} strokeWidth={1.9} aria-hidden className="text-ando-orange" />
            <Trans>Discussione</Trans>
          </h2>
          <ConnectMessageThread
            spaceId={spaceId}
            guestCredential={credential}
            className="min-h-0 flex-1"
          />
        </section>

        <p className="px-1 text-center text-[11px] text-ink-2">
          <Trans>Accesso ospite in sola lettura · solo le fonti condivise con te</Trans>
        </p>
      </div>
    </ConnectPublicPage>
  );
}
